import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Space, Spin, Typography } from 'antd';
import { ArrowLeftOutlined, ToolOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useConvertProductionRequestToWorkOrder } from '@/hooks/useWorkOrder';
import type { ConversionData } from '@/services/workOrder.service';
import * as prApi from '@/services/productionRequest.service';
import { SalesOrderToWorkOrderDialog } from '@/components/manufacturing';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';

const { Text } = Typography;

/**
 * Full-page flow: create work order(s) from a production request (same pattern as SalesOrderToWorkOrderPage).
 */
const ProductionRequestToWorkOrderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: pr, isLoading, error } = useQuery({
    queryKey: ['production-request', id],
    queryFn: () => prApi.getProductionRequest(id!),
    enabled: !!id,
  });
  const convertMutation = useConvertProductionRequestToWorkOrder();

  const itemsForConversion = useMemo(() => {
    const lines = pr?.lines ?? [];
    return lines
      .filter((l) => {
        const rem = Number(l.quantity) - Number(l.quantity_released_to_wo ?? 0);
        return rem > 0;
      })
      .map((l) => {
        const rem = Number(l.quantity) - Number(l.quantity_released_to_wo ?? 0);
        return {
          id: l.id,
          item_code: l.item?.item_code ?? l.manual_product_code ?? undefined,
          item_name: l.item?.item_name ?? l.manual_product_description ?? undefined,
          quantity: rem,
          ordered_quantity: rem,
          uom: l.uom,
          delivery_date: l.target_production_date ?? undefined,
          required_date: l.target_production_date ?? undefined,
        };
      });
  }, [pr]);

  const breadcrumbSegments = useMemo(
    () =>
      pr
        ? [
            { title: 'Manufacturing', path: '/mfg/dashboard' },
            { title: 'Production requests', path: '/mfg/production-requests' },
            { title: pr.request_number || 'Request', path: `/mfg/production-requests/${pr.id}` },
            { title: 'Create work order' },
          ]
        : null,
    [pr?.id, pr?.request_number]
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  if (!id) {
    return null;
  }

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !pr) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">Unable to load production request.</Text>
        <div style={{ marginTop: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/mfg/production-requests')}>
            Back to list
          </Button>
        </div>
      </div>
    );
  }

  if (pr.status !== 'Approved') {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">Production request must be approved before creating work orders.</Text>
        <div style={{ marginTop: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/mfg/production-requests/${pr.id}`)}>
            Back to request
          </Button>
        </div>
      </div>
    );
  }

  if (itemsForConversion.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="warning">No remaining quantity to release to work orders for this request.</Text>
        <div style={{ marginTop: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/mfg/production-requests/${pr.id}`)}>
            Back to request
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ margin: '-20px -20px 0', marginBottom: 0 }}>
      <WorkspaceLayout
        fullBleed
        header={
          <EnterprisePageHeader
            documentLabel="Manufacturing"
            documentNumber={pr.request_number}
            subtitle={
              <Space>
                <ToolOutlined />
                <Text type="secondary">Create work order from production request</Text>
              </Space>
            }
            primaryActions={
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/mfg/production-requests/${pr.id}`)}>
                Back to request
              </Button>
            }
          />
        }
      >
        <Card bordered={false} style={{ maxWidth: 1200, margin: '0 auto' }}>
          <SalesOrderToWorkOrderDialog
            variant="page"
            documentKind="production-request"
            defaultTargetWarehouseId={pr.warehouse_id}
            salesOrderId={pr.id}
            salesOrderNumber={pr.request_number}
            items={itemsForConversion}
            onClose={() => navigate(`/mfg/production-requests/${pr.id}`)}
            onSubmit={async (data: ConversionData) => {
              await convertMutation.mutateAsync({ productionRequestId: pr.id, data });
            }}
            onSuccess={() => navigate('/mfg/work-orders')}
          />
        </Card>
      </WorkspaceLayout>
    </div>
  );
};

export default ProductionRequestToWorkOrderPage;
